import { useState, useEffect } from 'react'
import { Book, Code, Zap, Copy, Check, ChevronDown, Key, Terminal, Shuffle } from 'lucide-react'

import { getAvailableModels, ModelConfig } from '../config/modelConfig'
import toast from 'react-hot-toast'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Param {
  name: string
  type: string
  required: boolean
  description: string
}

interface Endpoint {
  id: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  path: string
  description: string
  auth: string
  recommended?: boolean
  params?: Param[]
  examples: { label: string; lang: string; code: string }[]
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const BASE_URL = 'https://api.softitect.com'

const endpoints: Endpoint[] = [
  {
    id: 'chat-converse',
    method: 'POST',
    path: '/api/v1/client/chat/converse',
    description: 'Send a multi-turn conversation history and receive an AI response. Supports system prompts and alternating user/assistant turns.',
    auth: 'API Key - pass your key in the X-API-Key header',
    recommended: true,
    params: [
      { name: 'modelId', type: 'string', required: true, description: 'The model to use (e.g. "claude-3-haiku"). See Model Catalog for all valid values.' },
      { name: 'messages', type: 'array', required: true, description: 'Ordered conversation turns. Must contain at least one entry. The first message must have role "user".' },
      { name: 'messages[].role', type: 'string', required: true, description: 'Role of the message author. Allowed values: "user" or "assistant". The conversation must start with a "user" message.' },
      { name: 'messages[].content', type: 'string', required: true, description: 'Text content of the message turn.' },
      { name: 'systemPrompt', type: 'string', required: false, description: 'Optional top-level instruction that sets the model\'s behavior for the entire conversation.' },
    ],
    examples: [
      {
        label: 'cURL',
        lang: 'bash',
        code: `curl -X POST ${BASE_URL}/api/v1/client/chat/converse \\
  -H "X-API-Key: your-api-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "modelId": "claude-3-haiku",
    "systemPrompt": "You are a helpful assistant.",
    "messages": [
      { "role": "user", "content": "Hello, who are you?" },
      { "role": "assistant", "content": "I am an AI assistant. How can I help?" },
      { "role": "user", "content": "What can you help me with?" }
    ]
  }'`,
      },
      {
        label: 'Python',
        lang: 'python',
        code: `import requests

response = requests.post(
    "${BASE_URL}/api/v1/client/chat/converse",
    headers={
        "X-API-Key": "your-api-key-here",
        "Content-Type": "application/json"
    },
    json={
        "modelId": "claude-3-haiku",
        "systemPrompt": "You are a helpful assistant.",
        "messages": [
            {"role": "user", "content": "Hello, who are you?"},
            {"role": "assistant", "content": "I am an AI assistant. How can I help?"},
            {"role": "user", "content": "What can you help me with?"}
        ]
    }
)

print(response.json())`,
      },
      {
        label: 'Node.js',
        lang: 'javascript',
        code: `const response = await fetch("${BASE_URL}/api/v1/client/chat/converse", {
  method: "POST",
  headers: {
    "X-API-Key": "your-api-key-here",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    modelId: "claude-3-haiku",
    systemPrompt: "You are a helpful assistant.",
    messages: [
      { role: "user", content: "Hello, who are you?" },
      { role: "assistant", content: "I am an AI assistant. How can I help?" },
      { role: "user", content: "What can you help me with?" }
    ]
  })
});

const data = await response.json();
console.log(data);`,
      },
    ],
  },
  {
    id: 'chat-completion',
    method: 'POST',
    path: '/api/v1/client/chat/completion',
    description: 'Generate an AI response for a given message using a specified model.',
    auth: 'API Key - pass your key in the X-API-Key header',
    params: [
      { name: 'modelId', type: 'string', required: true, description: 'The model to use (e.g. "claude-3-haiku")' },
      { name: 'message', type: 'string', required: true, description: 'The user message or prompt to send' },
    ],
    examples: [
      {
        label: 'cURL',
        lang: 'bash',
        code: `curl -X POST ${BASE_URL}/api/v1/client/chat/completion \\
  -H "X-API-Key: your-api-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "modelId": "claude-3-haiku",
    "message": "Hello, how are you?"
  }'`,
      },
      {
        label: 'Python',
        lang: 'python',
        code: `import requests

response = requests.post(
    "${BASE_URL}/api/v1/client/chat/completion",
    headers={
        "X-API-Key": "your-api-key-here",
        "Content-Type": "application/json"
    },
    json={
        "modelId": "claude-3-haiku",
        "message": "Hello, how are you?"
    }
)

print(response.json())`,
      },
      {
        label: 'Node.js',
        lang: 'javascript',
        code: `const response = await fetch("${BASE_URL}/api/v1/client/chat/completion", {
  method: "POST",
  headers: {
    "X-API-Key": "your-api-key-here",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    modelId: "claude-3-haiku",
    message: "Hello, how are you?"
  })
});

const data = await response.json();
console.log(data);`,
      },
    ],
  },
  {
    id: 'health-check',
    method: 'GET',
    path: '/api/v1/client/health',
    description: 'Check the health and availability of the API service.',
    auth: 'None - publicly accessible',
    examples: [
      {
        label: 'cURL',
        lang: 'bash',
        code: `curl ${BASE_URL}/api/v1/client/health`,
      },
      {
        label: 'Python',
        lang: 'python',
        code: `import requests

response = requests.get("${BASE_URL}/api/v1/client/health")
print(response.json())`,
      },
    ],
  },
]

const METHOD_STYLES: Record<string, string> = {
  GET: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  POST: 'bg-success/10 text-success border-success/20',
  PUT: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  DELETE: 'bg-red-500/10 text-red-400 border-red-500/20',
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CopyButton({ text, id, copiedId, onCopy }: {
  text: string; id: string; copiedId: string | null; onCopy: (t: string, id: string) => void
}) {
  return (
    <button
      onClick={() => onCopy(text, id)}
      className="p-1.5 bg-panel hover:bg-border rounded-md transition-colors"
      title="Copy"
    >
      {copiedId === id
        ? <Check className="h-3.5 w-3.5 text-success" />
        : <Copy className="h-3.5 w-3.5 text-text-muted" />}
    </button>
  )
}

function EndpointCard({ endpoint, copiedId, onCopy }: {
  endpoint: Endpoint; copiedId: string | null; onCopy: (t: string, id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)

  return (
    <div className="border border-border rounded-xl overflow-hidden transition-all duration-200 hover:border-accent/30">
      {/* Header row - always visible */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-panel/50 transition-colors"
      >
        <span className={`shrink-0 px-2 py-0.5 text-xs font-bold rounded border ${METHOD_STYLES[endpoint.method]}`}>
          {endpoint.method}
        </span>
        <code className="flex-1 text-sm font-mono text-text truncate">{endpoint.path}</code>
        {endpoint.recommended && (
          <span className="shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-accent/10 text-accent border border-accent/20">
            Recommended
          </span>
        )}
        <span className="hidden sm:block text-xs text-text-muted mr-2 truncate max-w-xs">{endpoint.description}</span>
        <ChevronDown className={`shrink-0 h-4 w-4 text-text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Expanded content */}
      {open && (
        <div className="border-t border-border bg-bg/50">
          <div className="p-5 space-y-5">
            {/* Description + auth */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Description</p>
                <p className="text-sm text-text">{endpoint.description}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Authentication</p>
                <div className="flex items-center gap-2">
                  <Key className="h-3.5 w-3.5 text-accent shrink-0" />
                  <p className="text-sm text-text">{endpoint.auth}</p>
                </div>
              </div>
            </div>

            {/* Parameters */}
            {endpoint.params && endpoint.params.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Request Body</p>
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-panel">
                      <tr>
                        {['Field', 'Type', 'Required', 'Description'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-text-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {endpoint.params.map((p, i) => (
                        <tr key={p.name} className={i < endpoint.params!.length - 1 ? 'border-b border-border/50' : ''}>
                          <td className="py-2.5 px-3 font-mono text-xs text-accent">{p.name}</td>
                          <td className="py-2.5 px-3 text-xs text-text-muted">{p.type}</td>
                          <td className="py-2.5 px-3 text-xs">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${p.required ? 'bg-success/10 text-success' : 'bg-border text-text-muted'}`}>
                              {p.required ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-xs text-text">{p.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Code examples with tabs */}
            <div>
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Example Request</p>
              <div className="rounded-xl border border-border overflow-hidden">
                {/* Tab bar */}
                <div className="flex items-center gap-1 px-3 py-2 bg-panel border-b border-border">
                  <Terminal className="h-3.5 w-3.5 text-text-muted mr-1" />
                  {endpoint.examples.map((ex, i) => (
                    <button
                      key={ex.label}
                      onClick={() => setActiveTab(i)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${
                        activeTab === i
                          ? 'bg-accent text-white font-medium'
                          : 'text-text-muted hover:text-text hover:bg-border'
                      }`}
                    >
                      {ex.label}
                    </button>
                  ))}
                  <div className="flex-1" />
                  <CopyButton
                    text={endpoint.examples[activeTab].code}
                    id={`${endpoint.id}-${activeTab}`}
                    copiedId={copiedId}
                    onCopy={onCopy}
                  />
                </div>
                {/* Code block */}
                <pre className="p-4 overflow-x-auto text-xs font-mono text-text leading-relaxed bg-bg">
                  {endpoint.examples[activeTab].code}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function RouterModelCard({ model, copiedId, onCopy }: {
  model: ModelConfig; copiedId: string | null; onCopy: (t: string, id: string) => void
}) {
  return (
    <div className="p-5 rounded-xl bg-gradient-to-br from-accent/5 to-accent-2/5 border border-accent/30 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-accent/10 border border-accent/20 shrink-0">
          <Shuffle className="h-4 w-4 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-text">{model.displayName}</h3>
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-accent/10 text-accent border border-accent/20">
              Intelligent Router
            </span>
          </div>
          <p className="text-xs text-text-muted mt-1 leading-relaxed">{model.description}</p>
        </div>
      </div>

      {/* How it works — removed, shown once above the grid */}

      {/* Model pool */}
      <div>
        <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Model pool</p>
        <div className="flex gap-2 flex-wrap">
          {(model.routerCandidates ?? []).map(id => (
            <span key={id} className="px-2 py-1 rounded-lg bg-panel border border-border text-xs font-mono text-text-muted">
              {id}
            </span>
          ))}
        </div>
      </div>

      {/* Model ID + credits */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-panel border border-border">
          <code className="flex-1 text-xs font-mono text-accent truncate">{model.customId}</code>
          <CopyButton text={model.customId} id={model.customId} copiedId={copiedId} onCopy={onCopy} />
        </div>
        <div className="flex items-center gap-1.5 p-2 rounded-lg bg-accent/5 border border-accent/20">
          <Zap className="h-3 w-3 text-accent" />
          <span className="text-xs font-semibold text-accent">{model.defaultTokensPerCredit.toLocaleString()}</span>
          <span className="text-xs text-text-muted">tokens / credit</span>
        </div>
      </div>
    </div>
  )
}

function ModelFamilySection({ family, models, copiedId, onCopy }: {
  family: string; models: ModelConfig[]; copiedId: string | null; onCopy: (t: string, id: string) => void
}) {
  const provider = models[0]?.provider ?? ''

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-px flex-1 bg-border" />
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-panel border border-border">
          <span className="text-xs font-semibold text-text-muted">{provider}</span>
          <span className="text-xs text-text-muted">·</span>
          <span className="text-xs font-bold text-text">{family}</span>
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {models.map(model => (
          <div
            key={model.customId}
            className="p-4 rounded-xl bg-bg border border-border hover:border-accent/30 transition-all duration-200 flex flex-col gap-3"
          >
            <div>
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="text-sm font-bold text-text">{model.displayName}</h3>
              </div>
              <p className="text-xs text-text-muted leading-relaxed">{model.description}</p>
            </div>

            {/* Model ID with copy */}
            <div className="flex items-center gap-2 p-2 rounded-lg bg-panel border border-border">
              <code className="flex-1 text-xs font-mono text-accent truncate">{model.customId}</code>
              <CopyButton text={model.customId} id={model.customId} copiedId={copiedId} onCopy={onCopy} />
            </div>

            {/* Stats */}
            <div className="flex flex-col gap-1.5 text-xs">
              <div className="flex items-center gap-1.5 p-2 rounded-lg bg-accent/5 border border-accent/20">
                <Zap className="h-3 w-3 text-accent shrink-0" />
                <span className="font-semibold text-accent">{model.defaultTokensPerCredit.toLocaleString()}</span>
                <span className="text-text-muted">tokens / credit</span>
              </div>
              <div className="flex gap-1 flex-wrap">
                {model.capabilities.map(cap => (
                  <span key={cap} className="px-1.5 py-0.5 bg-success/10 text-success rounded border border-success/20 text-xs">
                    {cap}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function DocumentationPage() {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const models = getAvailableModels()

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    toast.success('Copied to clipboard')
    setTimeout(() => setCopiedId(null), 2000)
  }

  // Group models by family
  const modelsByFamily = models.reduce<Record<string, ModelConfig[]>>((acc, m) => {
    if (m.isRouter) return acc  // handled separately
    if (!acc[m.modelFamily]) acc[m.modelFamily] = []
    acc[m.modelFamily].push(m)
    return acc
  }, {})

  const routerModels = models.filter(m => m.isRouter)

  const navItems = [
    { id: 'overview', label: 'Overview', icon: Book },
    { id: 'endpoints', label: 'Endpoints', icon: Code },
    { id: 'models', label: 'Models', icon: Zap },
  ]

  const [activeSection, setActiveSection] = useState('overview')

  useEffect(() => {
    const observers = navItems.map(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return null
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id) },
        { rootMargin: '-20% 0px -70% 0px' }
      )
      observer.observe(el)
      return observer
    })
    return () => observers.forEach(o => o?.disconnect())
  }, [])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="animate-fade-in-up">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-gradient-to-br from-accent to-accent-2">
          <Book className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-text">
            API <span className="text-gradient">Documentation</span>
          </h1>
          <p className="text-text-muted mt-0.5 text-sm">
            Everything you need to integrate Softitect into your applications
          </p>
        </div>
      </div>

      <div className="flex gap-8 items-start">
        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-8">

          {/* Overview */}
          <section id="overview">
            <div className="card p-6 space-y-4">
              <h2 className="text-xl font-bold text-text">Overview</h2>
              <p className="text-sm text-text-muted leading-relaxed">
                The Softitect API gives you access to powerful AI models via a simple REST interface.
                Authenticate with your API key and start sending requests in minutes.
              </p>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-bg border border-border">
                <span className="text-xs text-text-muted font-medium shrink-0">Base URL</span>
                <code className="flex-1 text-xs font-mono text-accent">{BASE_URL}</code>
                <CopyButton text={BASE_URL} id="base-url" copiedId={copiedId} onCopy={copyToClipboard} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-bg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Key className="h-4 w-4 text-accent" />
                    <p className="text-sm font-semibold text-text">Authentication</p>
                  </div>
                  <p className="text-xs text-text-muted">Pass your API key in the <code className="text-accent">X-API-Key</code> request header.</p>
                </div>
                <div className="p-4 rounded-lg bg-bg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Code className="h-4 w-4 text-accent-2" />
                    <p className="text-sm font-semibold text-text">Format</p>
                  </div>
                  <p className="text-xs text-text-muted">All requests and responses use <code className="text-accent">application/json</code>.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Endpoints */}
          <section id="endpoints">
            <div className="card overflow-hidden">
              <div className="p-5 border-b border-border flex items-center gap-2">
                <Code className="h-5 w-5 text-accent" />
                <h2 className="text-xl font-bold text-text">Endpoints</h2>
                <span className="ml-auto text-xs text-text-muted">{endpoints.length} available</span>
              </div>
              <div className="p-5 space-y-3">
                {endpoints.map(ep => (
                  <EndpointCard
                    key={ep.id}
                    endpoint={ep}
                    copiedId={copiedId}
                    onCopy={copyToClipboard}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Models */}
          <section id="models">
            <div className="card overflow-hidden">
              <div className="p-5 border-b border-border flex items-center gap-2">
                <Zap className="h-5 w-5 text-accent-2" />
                <h2 className="text-xl font-bold text-text">Model Catalog</h2>
                <span className="ml-auto text-xs text-text-muted">{models.length} models</span>
              </div>
              <div className="p-5 space-y-6">
                {/* Router models first */}
                {routerModels.length > 0 && (() => {
                  const autoRouter = routerModels.find(m => m.customId === 'auto')
                  const otherRouters = routerModels.filter(m => m.customId !== 'auto')
                  return (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="h-px flex-1 bg-border" />
                        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/30">
                          <Shuffle className="h-3 w-3 text-accent" />
                          <span className="text-xs font-bold text-accent">Intelligent Routing</span>
                        </div>
                        <div className="h-px flex-1 bg-border" />
                      </div>

                      {/* How it works — once for all routers */}
                      <div className="p-4 rounded-xl bg-bg border border-border">
                        <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">How it works</p>
                        <div className="flex flex-col gap-2">
                          {[
                            { step: '1', text: <>Send a request with <code className="text-accent mx-1">modelId: "auto"</code> (or any router)</> },
                            { step: '2', text: 'The router scores prompt complexity and picks the best-fit model' },
                            { step: '3', text: 'You get the response — no extra config needed' },
                          ].map(({ step, text }) => (
                            <div key={step} className="flex items-center gap-2 text-xs text-text-muted">
                              <span className="w-5 h-5 rounded-full bg-accent/10 text-accent flex items-center justify-center font-bold shrink-0">{step}</span>
                              <span>{text}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Auto (all providers) — full width */}
                      {autoRouter && (
                        <RouterModelCard model={autoRouter} copiedId={copiedId} onCopy={copyToClipboard} />
                      )}

                      {/* Provider-specific routers */}
                      {otherRouters.length > 0 && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                          {otherRouters.map(m => (
                            <RouterModelCard key={m.customId} model={m} copiedId={copiedId} onCopy={copyToClipboard} />
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Regular models by family */}
                {Object.entries(modelsByFamily).map(([family, fModels]) => (
                  <ModelFamilySection
                    key={family}
                    family={family}
                    models={fModels}
                    copiedId={copiedId}
                    onCopy={copyToClipboard}
                  />
                ))}
              </div>
            </div>
          </section>

        </div>

        {/* Right sticky TOC nav */}
        <nav className="hidden xl:flex flex-col w-48 shrink-0 sticky top-6">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-widest mb-3 px-2">On this page</p>
          <div className="relative flex flex-col gap-0.5">
            {/* Active indicator bar */}
            <div
              className="absolute left-0 w-0.5 bg-accent rounded-full transition-all duration-300"
              style={{
                top: `${navItems.findIndex(n => n.id === activeSection) * 36 + 6}px`,
                height: '24px',
              }}
            />
            {navItems.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`flex items-center gap-2.5 pl-4 pr-2 py-2 rounded-lg text-sm transition-colors text-left ${
                  activeSection === id
                    ? 'text-text font-medium'
                    : 'text-text-muted hover:text-text hover:bg-panel/60'
                }`}
              >
                <Icon className={`h-3.5 w-3.5 shrink-0 transition-colors ${activeSection === id ? 'text-accent' : ''}`} />
                {label}
              </button>
            ))}
          </div>
        </nav>

      </div>
    </div>
  )
}
